import {
  type ReassistBillingItem,
  type ReassistBillingPurchase,
  type ReassistMonthlyBilling,
} from "../types";
import { billingTotals, requestFeeTotals } from "./billingTotals";

const HEADER = [
  "区分",
  "日付",
  "住所",
  "建物名",
  "内容",
  "手数料・税抜（円）",
  "経費・非課税（円）",
  "消費税（円）",
  "税込（円）",
  "状態",
];

/** CSV の1セル。カンマ・改行・引用符を含むときは引用符で囲み、式として解釈される先頭文字は無効にする。 */
const cell = (value: string | number | null): string => {
  if (value === null) return "";
  const text = String(value);
  // 表計算ソフトが式として実行しないよう、先頭が = + - @ とタブ・復帰の文字列には ' を付ける（数値は対象外）
  const safe =
    typeof value === "string" && /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return /[",\r\n]/.test(safe) ? `"${safe.replaceAll('"', '""')}"` : safe;
};

const row = (values: Array<string | number | null>): string =>
  values.map(cell).join(",");

/** 取得依頼1件の行。納品済みは納品日、納品前は依頼日を日付にする。金額が未定の暫定は空欄。 */
const requestRow = (item: ReassistBillingItem): string =>
  row([
    "取得依頼",
    item.deliveredOn ?? item.requestedOn,
    item.address,
    item.buildingName,
    `書類${item.documentCount}件`,
    item.handlingFee,
    item.expense,
    item.tax,
    item.total,
    item.deliveredOn === null
      ? "納品前（暫定）"
      : item.isProvisional
      ? "暫定"
      : "確定",
  ]);

/** 黒塗り資料の購入1件の行。クレジット（税別・1クレジット＝1円）を手数料に、消費税を足した額を税込にする。経費は無い。 */
const purchaseRow = (purchase: ReassistBillingPurchase): string =>
  row([
    "黒塗り資料の購入",
    purchase.purchasedOn,
    purchase.address,
    purchase.buildingName,
    purchase.documentType,
    purchase.credits,
    0,
    purchase.tax,
    purchase.credits + purchase.tax,
    "確定",
  ]);

/** 合計の行。区分の列に名前を置き、手数料・経費・消費税・税込の列に合計を置く。 */
const totalRow = (
  label: string,
  handlingFee: number,
  expense: number,
  tax: number,
  total: number
): string =>
  row([label, null, null, null, null, handlingFee, expense, tax, total, null]);

/**
 * 月の請求明細を CSV の文字列にする。取得依頼と黒塗り資料の購入を並べ、最後に依頼の合計・購入の合計・請求合計を付ける。
 * 手数料は税抜、経費は非課税、消費税は手数料にだけかかる。金額が未定の暫定は、合計に含めない。
 */
export const buildBillingCsv = (billing: ReassistMonthlyBilling): string => {
  const request = requestFeeTotals(billing);
  const totals = billingTotals(billing);
  return [
    row(HEADER),
    ...billing.items.map(requestRow),
    ...billing.purchases.map(purchaseRow),
    totalRow(
      "依頼の合計",
      request.handlingFee,
      request.expense,
      billing.taxTotal,
      billing.total
    ),
    totalRow(
      "黒塗り購入の合計",
      billing.purchaseCreditTotal,
      0,
      billing.purchaseTax,
      billing.purchaseTotal
    ),
    totalRow(
      "請求合計",
      totals.handlingFee,
      totals.expense,
      totals.tax,
      totals.total
    ),
  ].join("\r\n");
};

/** ダウンロードするファイル名。例: リアシスト請求明細_2026-10.csv */
export const billingCsvFileName = (billing: ReassistMonthlyBilling): string =>
  `リアシスト請求明細_${billing.month}.csv`;

/** CSV をブラウザでダウンロードする。Excel で文字化けしないよう、先頭に BOM を付ける。 */
export const downloadBillingCsv = (billing: ReassistMonthlyBilling): void => {
  const blob = new Blob(["﻿", buildBillingCsv(billing)], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = billingCsvFileName(billing);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};
